
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Modal, TextField, Alert } from '@dopomogai/ui';
import { Trash2 } from 'lucide-react';
import { agentService, isApiError } from '@dopomogai/api-client';
import type { UUID } from '@dopomogai/types';
import { toast } from '@dopomogai/ui';

type AgentDangerZoneCardProps = {
  agentId: UUID;
  agentName?: string | null;
  onDeleted?: () => void;
};

export const AgentDangerZoneCard: React.FC<AgentDangerZoneCardProps> = ({
  agentId,
  agentName,
  onDeleted,
}) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const requiredPhrase = useMemo(() => agentName?.trim() || '', [agentName]);
  const canDelete = requiredPhrase.length > 0 && confirmText.trim() === requiredPhrase;

  const handleOpenModal = () => {
    setConfirmText('');
    setErrorText(null);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    if (!isDeleting) {
      setIsModalOpen(false);
      setConfirmText('');
      setErrorText(null);
    }
  };

  const handleDelete = async () => {
    if (!agentId) return;
    if (!canDelete) {
      setErrorText('Please type the exact agent name to confirm.');
      return;
    }
    setIsDeleting(true);
    setErrorText(null);
    try {
      const res = await agentService.deleteAgent(agentId);
      if (isApiError(res)) {
        setErrorText(res.message || 'Failed to delete agent.');
        return;
      }
      toast.success('Agent deleted.');
      setIsModalOpen(false);
      if (typeof onDeleted === 'function') {
        onDeleted();
      } else {
        navigate('/app/agents');
      }
    } catch (err: any) {
      setErrorText(err?.message || 'Failed to delete agent.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card
      title="Danger zone"
      icon={<Trash2 size={18} />}
      headerBgColor="bg-rose-100"
      className="border-2 border-black"
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Deleting this agent will permanently remove it, its configuration, and associated data. This action cannot be undone.
        </p>
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-700">
            <span className="font-semibold">Agent ID:</span> <span className="font-mono">{agentId}</span>
          </div>
          <Button
            variant="danger"
            text="Delete agent"
            icon={<Trash2 size={14} />}
            onClick={handleOpenModal}
          />
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Confirm deletion"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" text="Cancel" onClick={handleCloseModal} disabled={isDeleting} />
            <Button
              variant="danger"
              text={isDeleting ? 'Deleting…' : 'Delete'}
              onClick={handleDelete}
              disabled={!canDelete || isDeleting}
            />
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            This will permanently delete agent <span className="font-semibold">{agentName || agentId}</span>.
            Please type the agent name below to confirm.
          </p>
          <TextField
            label="Type agent name to confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={agentName || ''}
            autoFocus
          />
          {errorText && <Alert type="error" message={errorText} />}
        </div>
      </Modal>
    </Card>
  );
};

export default AgentDangerZoneCard;